<template>
  <div class="general-settings-wrapper pa-2">
    <v-row>
      <!-- بخش انتخاب نوع کسب‌وکار (طراحی جدید و مدرن با Autocomplete) -->
      <v-col cols="12" class="mb-4">
        <div class="text-subtitle-2 mb-3 font-weight-bold text-grey-darken-2 d-flex align-center">
          <v-icon left size="20" color="primary" class="mr-1">mdi-store-cog-outline</v-icon>
          {{ $t('settings.business_type_title') || 'نوع کسب‌وکار خود را انتخاب کنید' }}
        </div>
        
        <v-autocomplete
          v-model="value.businessType"
          :items="businessTypes"
          item-text="text"
          item-value="value"
          outlined
          dense
          rounded
          color="primary"
          background-color="white"
          :placeholder="'مثلا: سوپرمارکت، کافه...'"
          hide-details
          class="modern-autocomplete elevation-0"
        >
          <!-- ظاهر آیتم انتخاب شده (چیزی که بعد از انتخاب نمایش داده می‌شود) -->
          <template v-slot:selection="{ item }">
            <v-chip
              color="primary"
              class="font-weight-medium px-4 glass-chip"
              dark
            >
              <v-icon left size="18">{{ item.icon }}</v-icon>
              {{ item.text }}
            </v-chip>
          </template>

          <!-- ظاهر لیست کشویی (منوی باز شده) -->
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" v-on="on" class="modern-list-item rounded-lg mx-2 my-1">
              <v-list-item-avatar color="blue lighten-5" size="36" class="mr-3">
                <v-icon color="primary" size="20">{{ item.icon }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ item.text }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- انتخاب واحد پول -->
      <v-col cols="12" sm="6">
        <v-select
          v-model="value.currency"
          :items="currencies"
          :label="$t('settings.currency_label')"
          prepend-inner-icon="mdi-cash-multiple"
          outlined
          dense
          hide-details
          class="modern-input"
          color="primary"
        ></v-select>
      </v-col>

      <!-- سوئیچ فیلتر محصولات -->
      <v-col cols="12" sm="6" class="d-flex align-center">
        <v-card 
          outlined 
          class="d-flex align-center w-100 px-4 py-1 modern-switch-card" 
        >
          <v-icon color="grey darken-1" left size="20" class="mr-2">mdi-filter-variant</v-icon>
          <span class="text-body-2 grey--text text--darken-2 ml-auto">{{ $t('settings.show_filter') }}</span>
          <v-switch
            v-model="value.showFilter"
            color="primary"
            inset
            dense
            hide-details
            class="mt-0 pt-0"
          ></v-switch>
        </v-card>
      </v-col>

      <!-- سوئیچ دسته‌بندی محصولات -->
      <v-col cols="12" sm="6" class="d-flex align-center mt-sm-2">
        <v-card 
          outlined 
          class="d-flex align-center w-100 px-4 py-1 modern-switch-card" 
        >
          <v-icon color="grey darken-1" left size="20" class="mr-2">mdi-shape-outline</v-icon>
          <span class="text-body-2 grey--text text--darken-2 ml-auto">{{ $t('settings.show_categories') }}</span>
          <v-switch
            v-model="value.showCategories"
            color="primary"
            inset
            dense
            hide-details
            class="mt-0 pt-0"
          ></v-switch>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "GeneralSettings",
  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({
        businessType: 'restaurant',
        currency: 'IRT',
        showFilter: true,
        showCategories: true,
        customLabels: { productsTitle: '', cartButton: '' }
      })
    }
  },
  computed: {
currencies() {
      const locale = this.$i18n.locale;
      
      const currencyMap = {
        'fa': [this.$t('settings.currencies.toman'), this.$t('settings.currencies.rial')],
        'ar_ae': ['دينار','ريال','درهم'],
        'ar_iq': ['دينار','ريال','درهم'],
        'ar': ['دينار','ريال','درهم'],
        'en_US': ['USD'],
        'en_GB': ['GBP'],
        'en': ['USD', 'EUR', 'GBP'], 
        'de': ['EUR'],
        'fr': ['EUR'],
        'ru': ['RUB'],
        'tr': ['TRY'],
        'hy': ['AMD'],
        'zh_CN': ['CNY'],
        'zh_TW': ['TWD']
      };
      return currencyMap[locale] || ['USD', 'EUR'];
    },    
    businessTypes() {
      return [ 
        { text: 'نانوایی', value: 'bakery', icon: 'mdi-baguette' },
        { text: 'شیرینی', value: 'sweets', icon: 'mdi-cupcake' },
        { text: 'کافه', value: 'cafe', icon: 'mdi-coffee-outline' },
        { text: 'سوپرمارکت', value: 'supermarket', icon: 'mdi-cart-outline' },
        { text: 'رستوران', value: 'restaurant', icon: 'mdi-silverware-fork-knife' },
        { text: 'گل و گیاه', value: 'florist', icon: 'mdi-flower' },
        { text: 'لبنیات', value: 'dairy', icon: 'mdi-cow' },
        { text: 'آبمیوه بستنی', value: 'juice_icecream', icon: 'mdi-glass-cocktail' },
        { text: 'پروتئین', value: 'protein', icon: 'mdi-food-steak' },
        { text: 'میوه', value: 'fruits', icon: 'mdi-fruit-watermelon' },
        { text: 'محصولات طبیعی', value: 'natural_products', icon: 'mdi-leaf' },
        { text: 'عطاری', value: 'herbalist', icon: 'mdi-mortar-pestle' },
        { text: 'قهوه و شکلات', value: 'coffee_chocolate', icon: 'mdi-candycane' },
        { text: 'آجیل', value: 'nuts', icon: 'mdi-peanut' },
        { text: 'سوسیس/کالباس', value: 'deli', icon: 'mdi-sausage' },
        { text: 'پت شاپ', value: 'pet_shop', icon: 'mdi-paw' },
        { text: 'سلامت و زیبایی', value: 'health_beauty', icon: 'mdi-heart-pulse' }
      ];
    },

    isFoodMenu() {
      return ['cafe', 'restaurant', 'fastfood', 'juice_icecream'].includes(this.value.businessType);
    }
  },
  watch: {
    currencies(newCurrencies) {
      if (!newCurrencies.includes(this.value.currency)) {
        this.$emit('input', { ...this.value, currency: newCurrencies[0] });
      }
    }
  }
};
</script>

<style scoped>
.general-settings-wrapper { 
  animation: fadeIn 0.4s ease-out; 
}

/* استایل‌های مدرن برای Autocomplete */
.modern-autocomplete ::v-deep .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important;
  transition: all 0.3s ease;
}

.modern-autocomplete ::v-deep .v-input__control > .v-input__slot:hover,
.modern-autocomplete.v-input--is-focused ::v-deep .v-input__control > .v-input__slot {
  border-color: var(--v-primary-base, #1976D2);
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.1) !important;
  transform: translateY(-2px);
}

.modern-list-item {
  transition: all 0.2s ease;
}

.modern-list-item:hover {
  background-color: #f0f7ff !important;
  transform: scale(1.01);
}

.glass-chip {
  backdrop-filter: blur(4px);
  background: linear-gradient(135deg, var(--v-primary-base, #1976D2) 0%, #42A5F5 100%) !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3) !important;
}

/* استایل سوییچ‌ها */
.modern-switch-card {
  border-color: #e0e0e0 !important; 
  height: 48px; 
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02) !important;
  transition: all 0.3s ease;
}

.modern-switch-card:hover {
  border-color: #bdbdbd !important;
  transform: translateY(-2px);
}

.modern-input { 
  transition: transform 0.2s ease; 
}
.modern-input ::v-deep .v-input__slot {
  border-radius: 12px !important;
}

@keyframes fadeIn { 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: translateY(0); } 
}
</style>
